import React, { useEffect, useState } from "react";
import { Option } from "../../components/Atomos/DropDown/DDMultiple";
import CatalogTableC from "../../components/Catalogs/CatalogTableC";
import { UseCountryList } from "../../hooks/Catalogs";
import { UseFirmList } from "../../hooks/Catalogs/useFirm";
import { UseRegionList } from "../../hooks/Catalogs/useRegionListOptions";
import useDataLoader2 from "../../hooks/useDataLoader2";
import PaginatedTable from "../../components/Organismos/Table/PaginatedTable";
import { formatTimeSpanToString } from "../../utils/functions";
import { getJurisdictionLabel } from "../../utils/jurisdiction/structuredJurisdiction";
import ShapeEdit from "../../components/Atomos/Icons/ShapeEdit";
import { useCityList } from "../../hooks/Catalogs/useCityList";
import { useStatesList } from "../../hooks/Catalogs/useStates";
import { defaultPageSize } from '../../utils/const';
import Filters, { IFilterData } from "../../components/Catalogs/Filters/Filters";
import PersistentLink from "../../components/Moleculas/PersistentLink";
import { exportAllDataLoader2 } from "../../utils/exportAllDataLoader2";

const Office: React.FC = () => {
  const { countryData } = UseCountryList();
  const { firmData } = UseFirmList();
  const { cityData } = useCityList();
  const { statesData } = useStatesList();
  const { regionData } = UseRegionList();
  const [statesByCountry, setStatesByCountry] = useState<any>([]);
  const [citiesByState, setCitiesByState] = useState<any>([]);
  const endpoint = '/office';
  const { 
    data, 
    totalCount, 
    fetchData, 
    setFilter, 
    sort, 
    setSort, 
    pagination, 
    setPagination,
    inputFilters,
    setInputFilters,
    filter
  } = useDataLoader2<any>({
    endpoint,
    defaultPageSize,
    defaultSorting: { field: "name", order: "asc" },
  });

  const columnsDef = React.useMemo(() => [
    { 
      id: 'count', 
      header: '#', 
      enableSorting: false,
      cell: ({ rowIndex }: { rowIndex: number; row: any; value: any }) => (
        <p>{rowIndex + 1 + pagination.pageIndex * pagination.pageSize}</p>
      )
    },
    { id: 'name', header: 'Name' },
    { id: 'officeType', header: 'Office Type' },
    { id: 'firm', header: 'Firm' },
    { id: 'jurisdiction', header: 'Jurisdiction' },
    { id: 'jurisdictionGroup', header: 'Jurisdiction Group' },
    { id: 'state', header: 'State' },
    { id: 'address', header: 'Address' },
    { id: 'city', header: 'City' },
    { id: 'country', header: 'Country' },
    { id: 'region', header: 'Region' },
    { id: 'countOfPeople', header: 'Professionals in Office' },
    { id: 'phoneNumber', header: 'Phone Number' },
    { id: 'yearFounded', header: 'Year Founded' },
    { id: 'createdDate', header: 'Created Date' },
    {
      id: 'edit', 
      header: 'Edit', 
      enableSorting: false,
      cell: ({ row, rowIndex }: { rowIndex: number; row: any; value: any }) => (
        <PersistentLink
          className="edit"
          to={`Edit/${row.id}`}
          state={data[rowIndex]}
          persistingData={{
            pagination,
            sort,
            filter, 
            inputFilters, 
          }}
        >
          Edit
          <div className="editsvg"><ShapeEdit /></div>
        </PersistentLink>
      )
    },
  ], [pagination.pageIndex, pagination.pageSize, sort, filter, inputFilters, data]);

  useEffect(() => {
    setStatesByCountry(statesData.filter((s: any) => 
      filter?.country?.value?.includes(s.country)));
  }, [filter?.country, statesData]);

  useEffect(() => {
    setCitiesByState(cityData.filter((c: any) => 
      filter?.state?.value?.includes(c.state)));
  }, [filter?.state, cityData]);

  const [officeTypeList] = useState<Option[]>([
    { id: "main", name: "Main" },
    { id: "additional", name: "Additional" },
  ]);
  
  const formatData = (data: any[]) => {
    const formatedData = data.map((d: any) => {
      return {
        ...d,
        firm: d.firmName,
        jurisdiction: getJurisdictionLabel(d),
        jurisdictionGroup: d.jurisdictionGroupName,
        address: d.addressName,
        city: d.cityName,
        state: d.stateName,
        country: d.countryName,
        phoneNumber: `${d.phoneNumber?.code ?? ""} ${d.phoneNumber?.basecode ?? ""} ${d.phoneNumber?.phone ?? ""}`,
        region: d.regionNames,
        createdDate: d.createdDate ? formatTimeSpanToString(d.createdDate) : '',
      }
    })
    return formatedData
  }

  const filtersData: IFilterData[] = React.useMemo(() => [
    {
      name: "Region",
      objectData: regionData?.map(r => ({id: r.id, name: r.name})) || [],
    },
    {
      name: "Country", 
      objectData: countryData?.map(c => ({id: c.id, name: c.name})) || [],
      comparison: "EQUAL",
    },
    {
      name: "State",
      objectData: statesByCountry || [],
      disabled: !statesByCountry?.length,
      comparison: "EQUAL",
    },
    {
      name: "City",
      objectData: citiesByState || [],
      disabled: !citiesByState?.length,
      comparison: "EQUAL",
    },
    {
      name: "Name",
      type: "text",
      comparison: "CONTAINS",
    },
    {
      name: "Firm",
      objectData: firmData?.map((f: any) => ({id:f.id, name: f.name})) || [],
      comparison: "EQUAL",
    },
    {
      name: "Office Type",
      fieldName: "officeType",
      objectData: officeTypeList || [],
      comparison: "BY_NAME",
      disabledWrite: true,
    },
  ], [regionData, countryData, statesByCountry, citiesByState, firmData, officeTypeList]);

  return (
    <>
        <CatalogTableC
          title={`Office Catalog`}
          catalogName={"OFFICES"}
          catalogShortName={"Office"}
          route={"Office"}
          totalCount={totalCount}
        >
          <Filters
            filtersData={filtersData} 
            filterValues={inputFilters}
            setFilterValues={setInputFilters} 
            Search={() => fetchData()}
            clearSelections={() => fetchData(true)}
            setValueForQuery={setFilter}
          />
          <PaginatedTable
            data={data || []}
            columnsDef={columnsDef}
            formatData={formatData}
            sorting={sort}
            onSortingChange={setSort}
            totalCount={totalCount || 0}
            pagination={pagination}
            onPaginationChange={setPagination}
            endpoint={endpoint}
            onExportAll={() =>
              exportAllDataLoader2<any>({
                endpoint,
                filter,
                sort,
                pageSize: 1000,
              })
            }
          />
        </CatalogTableC>
    </>
  );
};

export default Office;
